import React, { useState } from "react";
import api from "@/lib/api";
import { Mail, ArrowUpRight, Check } from "lucide-react";
import { toast } from "sonner";

export default function NewsletterCTA({ source = "blog" }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      const r = await api.post("/newsletter/subscribe", {
        email: email.trim(),
        name: name.trim(),
        source,
      });
      if (r.data?.already_subscribed) {
        toast.success("You're already on the list — thank you.");
      } else {
        toast.success("Welcome aboard. Quiet stories, never spam.");
      }
      setDone(true);
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(
        typeof msg === "string"
          ? msg
          : "Could not subscribe. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      data-testid="newsletter-cta"
      className="relative max-w-7xl mx-auto px-6 md:px-12 pb-12 md:pb-16"
    >
      <div className="relative overflow-hidden rounded-3xl bg-slate-900 text-white px-8 md:px-16 py-16 md:py-20">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-seafoam/25 blur-3xl" />
        <div className="absolute -bottom-32 -left-24 w-96 h-96 rounded-full bg-sky/15 blur-3xl" />

        <div className="relative grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/15 text-[10px] font-semibold uppercase tracking-[0.25em]">
              <Mail size={12} strokeWidth={1.6} /> The journal
            </div>
            <h3 className="mt-6 text-4xl md:text-5xl font-light tracking-tight leading-[1.05]">
              Quiet stories from <b className="font-bold">across Indonesia,</b>
              <br />in your inbox.
            </h3>
            <p className="mt-5 text-white/70 leading-relaxed max-w-lg">
              One letter a month: a new destination guide, a seasonal travel
              tip, and a single hand-picked itinerary. No spam, unsubscribe
              with one click.
            </p>
          </div>

          <div className="md:col-span-5">
            {done ? (
              <div
                data-testid="newsletter-success"
                className="flex flex-col items-start gap-4 p-6 rounded-2xl bg-white/10 border border-white/15"
              >
                <div className="w-12 h-12 rounded-full bg-seafoam/30 border border-seafoam/50 flex items-center justify-center">
                  <Check size={20} strokeWidth={1.8} className="text-white" />
                </div>
                <div>
                  <div className="text-lg font-medium">You&apos;re in.</div>
                  <p className="mt-1 text-sm text-white/70">
                    Watch your inbox for the next dispatch — we send the first
                    of every month.
                  </p>
                </div>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                className="flex flex-col gap-3"
                data-testid="newsletter-form"
              >
                <input
                  data-testid="newsletter-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name (optional)"
                  className="w-full px-5 py-3.5 rounded-full bg-white/10 border border-white/20 text-white placeholder:text-white/50 outline-none focus:bg-white/15 focus:border-white/40 transition-all"
                />
                <input
                  data-testid="newsletter-email"
                  required
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full px-5 py-3.5 rounded-full bg-white/10 border border-white/20 text-white placeholder:text-white/50 outline-none focus:bg-white/15 focus:border-white/40 transition-all"
                />
                <button
                  data-testid="newsletter-submit"
                  type="submit"
                  disabled={submitting}
                  className="btn-pill bg-white text-slate-900 hover:bg-stone-100 text-sm justify-center shadow-lg disabled:opacity-60"
                >
                  {submitting ? "Subscribing…" : "Subscribe to the journal"}
                  <ArrowUpRight size={15} strokeWidth={1.6} />
                </button>
                <p className="text-[11px] text-white/50 tracking-wide text-center mt-1">
                  We will never share your address.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
